import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { getLocations } from '../../lib/sanity';
import Container from '../../components/shared/Container';
const MapboxMap = lazy(() => import('../../components/shared/MapboxMap'));
import LocationCard from '../../components/shared/LocationCard';
import LocationCardSkeleton from '../../components/shared/LocationCardSkeleton';
import SEO from '../../components/shared/SEO';
import StructuredData from '../../components/shared/StructuredData';
import { debounce } from 'lodash';
import { useLocation } from '../../context/LocationContext';
import { useWaitlist } from '../../context/WaitlistContext';
import { useOrderConfirmation } from '../../context/OrderConfirmationContext';
import { generateBreadcrumbSchema } from '../../utils/schema';
import { sortLocationsByDefault, calculateDistance } from '../../utils/locationSorting';
import { DIRECT_LINK_SLUGS } from '../../utils/waitlist';

const LOCATION_FOCUS_ZOOM = 3;

export default function LocationsPage() {
  // Get global location context
  const { 
    userLocation: globalUserLocation, 
    selectedLocation: globalSelectedLocation,
    updateUserLocation,
    selectLocation 
  } = useLocation();
  
  // Get waitlist context
  const { setWaitlistUrlAndOpenModal } = useWaitlist();
  
  // Get order confirmation context
  const { confirmOrder } = useOrderConfirmation();
  
  const [locations, setLocations] = useState([]);
  const [sortedLocations, setSortedLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('card'); // 'card' or 'map'
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Address search and location states - initialize from global state
  const [address, setAddress] = useState('');
  const [userLocation, setUserLocation] = useState(globalUserLocation);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(globalSelectedLocation);
  const [mapViewState, setMapViewState] = useState(null);
  
  // Refs
  const mapRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    async function fetchLocations() {
      try {
        setLoading(true);
        const locationsData = await getLocations();
        const sortedData = sortLocationsByDefault(locationsData || []);
        setLocations(sortedData);
        setSortedLocations(sortedData);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchLocations();
  }, []);

  // Sort locations by distance when user location changes - SAME PATTERN AS MODAL
  useEffect(() => {
    if (!userLocation) {
      // Without user location, use default sorting and prioritize selected location if it exists
      const defaultSorted = sortLocationsByDefault(locations);
      
      if (globalSelectedLocation) {
        const selectedIndex = defaultSorted.findIndex(loc => loc._id === globalSelectedLocation._id);
        if (selectedIndex > 0) {
          const reordered = [...defaultSorted];
          const selectedLoc = reordered.splice(selectedIndex, 1)[0];
          reordered.unshift(selectedLoc);
          setSortedLocations(reordered);
          setSelectedLocation(selectedLoc);
        } else {
          setSortedLocations(defaultSorted);
          if (selectedIndex === 0) {
            setSelectedLocation(defaultSorted[0]);
          }
        }
      } else {
        setSortedLocations(defaultSorted);
      }
      return;
    }
    
    // Calculate distances for all locations
    const locationsWithDistance = locations.map(location => {
      if (!location.coordinates) return { ...location, distance: null };
      
      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        location.coordinates.lat,
        location.coordinates.lng
      );
      
      return { ...location, distance };
    });
    
    // Sort by distance (null values at the end)
    let sorted = locationsWithDistance.sort((a, b) => {
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });
    
    // If there's a globally selected location, move it to the front
    if (globalSelectedLocation) {
      const selectedIndex = sorted.findIndex(loc => loc._id === globalSelectedLocation._id);
      if (selectedIndex > 0) {
        const selectedLoc = sorted.splice(selectedIndex, 1)[0];
        sorted.unshift(selectedLoc);
      }
    }
    
    setSortedLocations(sorted);
    
    // Auto-select logic: use global selected location if exists, otherwise use closest
    let locationToFocus = null;
    
    if (globalSelectedLocation) {
      const foundLocation = sorted.find(loc => loc._id === globalSelectedLocation._id);
      if (foundLocation) {
        setSelectedLocation(foundLocation);
        locationToFocus = foundLocation;
      }
    } else if (sorted.length > 0 && sorted[0].distance !== null) {
      const closestLocation = sorted[0];
      setSelectedLocation(closestLocation);
      selectLocation(closestLocation); // Update global context
      locationToFocus = closestLocation;
    }
    
    // Focus the map on the selected location
    if (locationToFocus && locationToFocus.coordinates) {
      if (mapRef && mapRef.current) {
        mapRef.current.flyTo({
          center: [locationToFocus.coordinates.lng, locationToFocus.coordinates.lat],
          zoom: LOCATION_FOCUS_ZOOM,
          duration: 500,
          essential: true
        });
      } else {
        setMapViewState({
          longitude: locationToFocus.coordinates.lng,
          latitude: locationToFocus.coordinates.lat,
          zoom: LOCATION_FOCUS_ZOOM
        });
      }
    }
  }, [userLocation, locations, globalSelectedLocation, selectLocation]);

  // Handle clicks outside the suggestions dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    
    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  // Handle "Use my location" button click
  const handleUseMyLocation = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setIsSearching(true);
    setError(null);
    
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setIsSearching(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newUserLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setUserLocation(newUserLocation);
        updateUserLocation(newUserLocation); // Update global context
        setIsSearching(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setError('Unable to retrieve your location. Please try entering your address instead.');
        setIsSearching(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Fetch address suggestions from Mapbox
  const fetchAddressSuggestions = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoibWl0aHJpbC1tZWRpYSIsImEiOiJjazhvb211cWIxYjZhM2Vxc3NnZm9hMmJzIn0.qMq9yOzeUkajyvm1AfTk7Q'}&country=US&types=address,place,locality,neighborhood,postcode&limit=5`
        );
        
        const data = await response.json();
        
        if (data.features && data.features.length > 0) {
          setSuggestions(data.features);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300),
    []
  );

  // Handle address input change
  const handleAddressChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    fetchAddressSuggestions(value);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    setAddress(suggestion.place_name);
    setSuggestions([]);
    setShowSuggestions(false);
    
    const [longitude, latitude] = suggestion.center;
    const newUserLocation = { latitude, longitude };
    setUserLocation(newUserLocation);
    updateUserLocation(newUserLocation); // Update global context
  };

  // Handle address search form submission
  const handleAddressSearch = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!address.trim()) {
      setError('Please enter an address');
      return;
    }
    
    setIsSearching(true);
    setError(null);
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoibWl0aHJpbC1tZWRpYSIsImEiOiJjazhvb211cWIxYjZhM2Vxc3NnZm9hMmJzIn0.qMq9yOzeUkajyvm1AfTk7Q'}&country=US`
      );
      
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [longitude, latitude] = data.features[0].center;
        const newUserLocation = { latitude, longitude };
        setUserLocation(newUserLocation);
        updateUserLocation(newUserLocation); // Update global context
      } else {
        setError('Address not found. Please try a different address.');
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      setError('Error finding address. Please try again.');
    } finally {
      setIsSearching(false);
      setShowSuggestions(false);
    }
  };

  return (
    <div className="bg-brand-cream">
      <SEO 
        title="Find a Location Near You | The Original Pancake House" 
        description="Find an Original Pancake House restaurant near you. Discover locations across Washington, Oregon, New York, and Indiana. Get directions, view hours, order online, or join our waitlist at your nearest breakfast and brunch location."
        openGraph={{
          image: "/oph-social-share3.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Locations', url: '/locations' }
      ])} />
      
      {/* Hero Section */}
      <div className="bg-brand-cream text-brand-navy pt-32 pb-12">
        <Container>
          <div className="text-left">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading">Our Locations</h1>
            <p className="text-xl">
              Discover Original Pancake House restaurants across the country. Find your nearest location for breakfast, brunch, and our famous pancakes.
            </p>
          </div>
        </Container>
      </div>

      {/* Search Bar and View Tabs */}
      <div className="bg-brand-cream py-8">
        <Container>
          {/* Address Search and View Tabs on same line */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-6">
            {/* Address Search - Left aligned */}
            <div className="flex-1 w-full lg:max-w-2xl">
              <form onSubmit={handleAddressSearch} className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <div className="relative">
                    <input
                      type="text"
                      id="address"
                      value={address}
                      onChange={handleAddressChange}
                      placeholder="Enter your address or zip code"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy"
                      autoComplete="off"
                    />
              <button
                      type="submit"
                      className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-brand-navy"
                      disabled={isSearching}
                    >
                      {isSearching ? (
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      )}
              </button>
                  </div>
                  
                  {/* Address Suggestions Dropdown */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div 
                      ref={suggestionsRef}
                      className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm border border-gray-200"
                    >
                      {suggestions.map((suggestion) => (
                        <div
                          key={suggestion.id}
                          className="cursor-pointer select-none relative py-2 px-3 hover:bg-gray-100"
                          onClick={() => handleSuggestionSelect(suggestion)}
                        >
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-sm truncate">{suggestion.place_name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <button
                  type="button"
                  onClick={handleUseMyLocation}
                  className="bg-brand-navy text-white px-6 py-3 rounded-lg hover:bg-brand-navy/90 transition-colors flex items-center justify-center font-medium whitespace-nowrap"
                  disabled={isSearching}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Use My Location
                </button>
              </form>
              
              {/* Error Message */}
              {error && (
                <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>

            {/* View Tabs - Full width on mobile, inline on desktop */}
            <div className="w-full lg:w-auto flex lg:inline-flex rounded-lg border border-gray-300 overflow-hidden shrink-0">
              <button
                onClick={() => setActiveView('card')}
                className={`flex-1 lg:flex-none px-6 py-3 font-medium text-sm transition-all flex items-center justify-center ${
                  activeView === 'card' 
                    ? 'bg-brand-navy text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Card View
              </button>
              <button
                onClick={() => setActiveView('map')}
                className={`flex-1 lg:flex-none px-6 py-3 font-medium text-sm transition-all flex items-center justify-center ${
                  activeView === 'map' 
                    ? 'bg-brand-navy text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Map View
              </button>
            </div>
          </div>

          {/* Map View */}
          {activeView === 'map' && (
            <div className="bg-white rounded-lg overflow-hidden shadow-lg mb-8">
              <div className="flex flex-col-reverse md:flex-row h-auto md:h-[700px]">
                {/* Left Side - Location List (shown second on mobile) */}
                <div className="w-full md:w-2/5 p-6 overflow-y-auto md:border-r border-gray-200">
                  <h3 className="text-lg font-medium text-brand-navy mb-4">Select a Location</h3>
                  <div className="space-y-4">
                    {sortedLocations.map((location) => (
                      <div
                        key={location._id}
                        className={`p-4 border rounded-md hover:shadow-md transition-all cursor-pointer ${
                          selectedLocation && selectedLocation._id === location._id
                            ? 'border-brand-orange bg-blue-50 shadow-md'
                            : 'border-gray-200'
                        }`}
                        onClick={() => {
                          setSelectedLocation(location);
                          selectLocation(location); // Update global context
                          // Update map view to focus on selected location
                          if (location.coordinates && mapRef && mapRef.current) {
                            mapRef.current.flyTo({
                              center: [location.coordinates.lng, location.coordinates.lat],
                              zoom: LOCATION_FOCUS_ZOOM,
                              duration: 500,
                              essential: true
                            });
                          }
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center">
                              <h4 className="font-medium text-brand-navy">{location.name}</h4>
                              {globalSelectedLocation && location._id === globalSelectedLocation._id && (
                                <span className="ml-2 text-xs font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                  Selected
                                </span>
                              )}
                            </div>
                            {location.address && (
                              <p className="text-xs text-gray-500 mt-1">
                                {location.address.street}<br />
                                {location.address.city}, {location.address.state} {location.address.zipCode}
                              </p>
                            )}
                          </div>
                          {location.distance !== null && location.distance !== undefined && (
                            <span className="text-xs font-medium bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                              {location.distance} mi
                            </span>
                          )}
                        </div>
                        
                        {/* Action Links */}
                        <div className="mt-3 flex flex-wrap gap-2 items-center">
                          {location.onlineOrderingUrl && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmOrder(location.onlineOrderingUrl, location.name);
                              }}
                              className="text-sm font-medium text-brand-navy hover:text-brand-orange transition-colors flex items-center whitespace-nowrap"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                              </svg>
                              Order Online
                            </button>
                          )}
                          
                          {location.onlineReservationsUrl && (
                            (isMobile || DIRECT_LINK_SLUGS.includes(location.slug)) ? (
                              <a
                                href={location.onlineReservationsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-brand-navy hover:text-brand-orange transition-colors flex items-center whitespace-nowrap"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Join Waitlist
                              </a>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setWaitlistUrlAndOpenModal(location.onlineReservationsUrl);
                                }}
                                className="text-sm font-medium text-brand-navy hover:text-brand-orange transition-colors flex items-center whitespace-nowrap"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Join Waitlist
                              </button>
                            )
                          )}
                          
                          <Link 
                            to={`/locations/${location.slug || ''}`}
                            className="text-sm font-medium text-gray-600 hover:text-brand-navy transition-colors flex items-center whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View Location Details
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Right Side - Map (shown first on mobile) */}
                <div className="w-full md:w-3/5 aspect-video md:aspect-auto md:h-full relative">
                  <Suspense fallback={
                    <div className="h-full w-full flex items-center justify-center bg-gray-100">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-navy"></div>
                    </div>
                  }>
                    <MapboxMap 
                    locations={userLocation ? 
                      [...sortedLocations, {
                        _id: 'user-location',
                        name: 'Your Location',
                        coordinates: {
                          lat: userLocation.latitude,
                          lng: userLocation.longitude
                        },
                        isUserLocation: true
                      }] : sortedLocations
                    }
                    initialViewState={mapViewState}
                    width="100%"
                    height="100%"
                    selectedLocationId={selectedLocation ? selectedLocation._id : null}
                    onWaitlistClick={setWaitlistUrlAndOpenModal}
                    onMapLoaded={(map) => {
                      mapRef.current = map;
                      
                      // If we have a selected location when the map loads, focus on it
                      if (selectedLocation && selectedLocation.coordinates) {
                        setTimeout(() => {
                          map.flyTo({
                            center: [selectedLocation.coordinates.lng, selectedLocation.coordinates.lat],
                            zoom: LOCATION_FOCUS_ZOOM,
                            duration: 0,
                            essential: true
                          });
                        }, 100);
                      }
                    }}
                    />
                  </Suspense>
                </div>
              </div>
            </div>
          )}

          {/* Card View */}
          {activeView === 'card' && (
            <>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, index) => (
                    <LocationCardSkeleton key={index} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedLocations.map((location, index) => (
                    <LocationCard 
                      key={location._id} 
                      location={location} 
                      isClosest={globalSelectedLocation && location._id === globalSelectedLocation._id}
                    />
                  ))}
                </div>
              )}
              
              {!loading && sortedLocations.length === 0 && (
                <div className="text-center py-12">
                  <h3 className="text-xl font-medium text-gray-600 font-heading">No locations found</h3>
                  <p className="mt-2 text-gray-500">Please try a different search</p>
                </div>
              )}
            </>
          )}
        </Container>
      </div>

    </div>
  );
}
